import { useState, useRef } from 'react';
import type { ExtractedMenu } from '../types';
import { api } from '../api/client';
import { WEEKDAY_NAMES, fromISO } from '../utils/dates';
import { formatAllergens } from '../utils/allergens';

interface MenuIngestionModalProps {
  onClose: () => void;
  onSaved: () => void;
  addToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

export function MenuIngestionModal({ onClose, onSaved, addToast }: MenuIngestionModalProps) {
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extracted, setExtracted] = useState<ExtractedMenu | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      addToast('Bitte nur Bilddateien hochladen.', 'error');
      return;
    }

    setPreviewUrl(URL.createObjectURL(file));
    setExtracting(true);
    setExtracted(null);

    try {
      const base64 = await fileToBase64(file);
      const result = await api.menus.ingest(base64, file.type);
      setExtracted(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Lesen';
      addToast(`Fehler: ${message}`, 'error');
    } finally {
      setExtracting(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleSave = async () => {
    if (!extracted) return;
    setSaving(true);
    try {
      await api.menus.save(extracted.week);
      addToast('Speiseplan erfolgreich gespeichert!', 'success');
      onSaved();
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Speichern';
      addToast(`Fehler: ${message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const getWeekdayName = (dateISO: string): string => {
    const date = fromISO(dateISO);
    const day = date.getDay();
    const idx = day === 0 ? 6 : day - 1;
    return WEEKDAY_NAMES[idx] || dateISO;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-card modal-card--ingestion"
        style={{ maxWidth: '28rem', maxHeight: 'calc(100vh - 2rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <div className="modal-title">Speiseplan einlesen</div>
            <div className="modal-subtitle">Screenshot hochladen – KI liest den Speiseplan aus</div>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Schließen" />
        </div>

        <div className="modal-body">
          {!extracted && (
            <>
              <div
                className={`upload-area ${dragOver ? 'drag-over' : ''}`}
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const file = e.dataTransfer.files[0];
                  if (file) handleFile(file);
                }}
              >
                {extracting ? (
                  <>
                    <div className="upload-icon">⏳</div>
                    <div>KI liest den Speiseplan…</div>
                  </>
                ) : (
                  <>
                    <div className="upload-icon">📸</div>
                    <div>Speiseplan-Screenshot hier ablegen oder klicken</div>
                    <div style={{ fontSize: '0.75rem', marginTop: '0.375rem', color: 'var(--text-lighter)' }}>
                      PNG, JPG, WEBP bis 20 MB
                    </div>
                  </>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                }}
              />
              {previewUrl && !extracting && (
                <img
                  src={previewUrl}
                  alt="Vorschau"
                  style={{ width: '100%', borderRadius: '0.75rem', maxHeight: '12.5rem', objectFit: 'contain', border: '1px solid var(--border)' }}
                />
              )}
            </>
          )}

          {extracted && (
            <>
              <div className="info-banner success">
                <span className="info-banner-icon">✓</span>
                <span>{extracted.week.length} Tage erfolgreich ausgelesen. Bitte prüfen und bestätigen.</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                {extracted.week.map((day) => (
                  <div key={day.date} className="ingestion-day-card">
                    <div className="ingestion-day-header">
                      {getWeekdayName(day.date)} · {day.date}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.375rem' }}>
                      <div style={{ background: 'var(--card-bg)', borderRadius: '0.375rem', padding: '0.375rem 0.5rem' }}>
                        <div style={{ fontSize: '0.563rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.125rem' }}>Gericht 1</div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>{day.dish1.name}</div>
                        {day.dish1.allergens.length > 0 && (
                          <div style={{ fontSize: '0.563rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                            {formatAllergens(day.dish1.allergens)}
                          </div>
                        )}
                      </div>
                      <div style={{ background: 'var(--card-bg)', borderRadius: '0.375rem', padding: '0.375rem 0.5rem' }}>
                        <div style={{ fontSize: '0.563rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.125rem' }}>Gericht 2</div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>{day.dish2.name}</div>
                        {day.dish2.diet && (
                          <div className="dish-btn-diet" style={{ marginTop: '0.125rem' }}>
                            {day.dish2.diet === 'vegan' ? 'vegan' : 'vegetarisch'}
                          </div>
                        )}
                        {day.dish2.allergens.length > 0 && (
                          <div style={{ fontSize: '0.563rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                            {formatAllergens(day.dish2.allergens)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-ghost"
                  onClick={() => { setExtracted(null); setPreviewUrl(null); }}
                >
                  Neu hochladen
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Wird gespeichert…' : 'Speiseplan speichern'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
